<script lang="ts">
  import { Card, Button, Tooltip } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faTimes, faUpload, faFolder } from "@fortawesome/free-solid-svg-icons";
  import { onMount, onDestroy } from "svelte";
  import type { Project, ProjectUploadsData, ProjectUpload } from "../../services/api/types.js";
  import { getProject, getProjectUploads, uploadProjectFiles, initializeProjectUpload } from "../../services/api/data-handler.js";
  import JSZip from "jszip";
  import { Progressbar } from "flowbite-svelte";
  import { uploadStore } from "../../services/stores/upload-store.js";
  import { userStore } from "../../services/stores/user-store.js";
  import { formatDate, formatTime } from "../../helpers/helpers.js";
  import { logError, logInfo, logWarning } from "../../services/logging/log-handler.js";

  $: progress = isComplete
    ? 100
    : $uploadStore.totalChunks
      ? ($uploadStore.chunkIndex / $uploadStore.totalChunks) * 100
      : 0;
  $: isUploading = $uploadStore.status === "uploading";
  $: isComplete = $uploadStore.status === "completed";

  $: currentUser = $userStore;
  $: nextVersion = uploadHistory?.project_uploads?.length
    ? Math.max(...uploadHistory.project_uploads.map((u) => u.version)) + 1
    : 1;

  export let project_id: string;
  let project: Project;
  let folderFiles: Array<{ name: string; path: string; file: File }> = [];
  let isDragging = false;
  let uploadHistory: ProjectUploadsData = { project_uploads: [] };

  // Add this to track the pending upload version
  let pendingUploadVersion: number | null = null;

  let fileInput: HTMLInputElement;

  let intervalId: NodeJS.Timeout | undefined;

  onMount(() => {
    loadUploads();
    //intervalId = setInterval(loadUploads, 15000);
  });

  let activeUploads = new Set();
  let isUploadInProgress = false; // Add this flag to prevent multiple uploads

  async function initialize() {
    const projectData = await getProject(project_id);
    project = projectData.project;
  }

  $: if (project_id) {
    initialize();
  }

  const addFilesToUpload = (
    files: Array<{ name: string; path: string; file: File }>
  ) => {
    folderFiles = [...folderFiles, ...files];

    // Only create new pending upload if one doesn't exist
    if (pendingUploadVersion === null) {
      if (!currentUser?.user?.user_id) {
        logError(new Error("Cannot create upload: No valid user ID"), "FilesUpload", undefined);
        return;
      }
      pendingUploadVersion = nextVersion;
      uploadHistory = {
        ...uploadHistory,
        project_uploads: [
          ...(uploadHistory?.project_uploads || []),
          {
            version: pendingUploadVersion,
            upload_by: currentUser.user.user_id,
            upload_by_name: currentUser.user.name,
            ready: false,
            document_count: 0,
            metadata: {
              timestamps: {
                started: new Date().toISOString(),
              }
            } as Record<string, any>,
          } as ProjectUpload,
        ],
      };
    }
  };

  const handleFolderSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    folderFiles = [];

    const processedFiles = Array.from(input.files)
      .filter((file) => !file.name.startsWith(".")) // Ignore dot files
      .map((file) => ({
        name: file.name,
        path: file.webkitRelativePath,
        file: file,
      }));

    addFilesToUpload(processedFiles);
  };

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    isDragging = false;
    folderFiles = [];

    if (event.dataTransfer?.items) {
      const firstItem = event.dataTransfer.items[0].webkitGetAsEntry();
      if (!firstItem?.isDirectory) {
        logError(new Error("Only directory drops are allowed"), "FilesUpload", undefined);
        return;
      }
      
      const rootFolder = firstItem.name;

      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        const item = event.dataTransfer.items[i].webkitGetAsEntry();
        if (item?.isDirectory) {
          await readDirectory(item, rootFolder);
        }
      }
    }
  };

  const readDirectory = async (
    entry: any,
    rootFolder: string,
    currentPath: string = ""
  ) => {
    const reader = entry.createReader();
    const entries = await new Promise((resolve) =>
      reader.readEntries((results: any[]) => resolve(results))
    );

    for (const subEntry of entries as any[]) {
      // Skip dot files and directories
      if (subEntry.name.startsWith(".")) continue;

      // Build the current path
      const entryPath = currentPath
        ? `${currentPath}/${subEntry.name}`
        : subEntry.name;

      if (subEntry.isFile) {
        const file = await new Promise<File>((resolve) =>
          subEntry.file((f: File) => resolve(f))
        );

        // Construct the full relative path
        const relativePath =
          entry.name === rootFolder
            ? `${rootFolder}/${entryPath}`
            : `${rootFolder}/${entryPath}`;

        addFilesToUpload([
          {
            name: file.name,
            path: relativePath,
            file: file,
          },
        ]);
      } else if (subEntry.isDirectory) {
        // Pass the accumulated path to preserve folder structure
        await readDirectory(subEntry, rootFolder, entryPath);
      }
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    isDragging = true;
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    isDragging = false;
  };

  async function loadUploads() {
    try {
      const uploadsData = await getProjectUploads(project_id);

      if (uploadsData.project_uploads) {
        const inProgressUploads = uploadsData.project_uploads.filter(
          (upload) =>
            upload.metadata.timestamps.finished === "Pending" &&
            upload.metadata.timestamps.uploaded !== "Pending" // Has started uploading
        );

        inProgressUploads.forEach((upload) => {
          activeUploads.add(upload.version);
        });

        if (inProgressUploads.length > 0 && !intervalId) {
          intervalId = setInterval(loadUploads, 15000);
        }
      }

      // Preserve pending upload if it exists
      if (pendingUploadVersion !== null) {
        const pendingUpload = uploadHistory?.project_uploads?.find(
          (upload) => upload.version === pendingUploadVersion
        );
        if (pendingUpload) {
          uploadsData.project_uploads = [
            pendingUpload,
            ...(uploadsData.project_uploads || []),
          ];
        }
      }

      uploadHistory = uploadsData;
    } catch (error) {
      logError(error, "FilesUpload", undefined, { message: "Error loading uploads" });
    }
  }

  onDestroy(() => {
    // Only clear interval if we don't have active uploads
    if (intervalId && activeUploads.size === 0) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
  });

  async function handleUpload() {
    if (!folderFiles.length || isUploadInProgress || activeUploads.size > 0) return; // Prevent multiple uploads and check for active uploads
    
    isUploadInProgress = true; // Set flag to prevent multiple uploads
    let version = 0;
    try {
      version = await initializeProjectUpload(project_id);
      
      activeUploads.add(version);

      // Create zip file
      const zip = new JSZip();
      folderFiles.forEach(({ path, file }) => {
        zip.file(path, file);
      });

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFile = new File([zipBlob], "project.zip", {
        type: "application/zip",
      });

      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }

      const success = await uploadProjectFiles(project_id, zipFile, version);

      if (success) {
        activeUploads.delete(version);

        if (uploadHistory?.project_uploads) {
          uploadHistory = {
            ...uploadHistory,
            project_uploads: uploadHistory.project_uploads.filter(
              (upload) => upload.version !== pendingUploadVersion
            ),
          };
        }

        pendingUploadVersion = null;

        const newUploadsData = await getProjectUploads(project_id);
        uploadHistory = newUploadsData;

        intervalId = setInterval(loadUploads, 15000);

        folderFiles = [];
        if (fileInput) fileInput.value = "";
      }
    } catch (error) {
      logError(error, "FilesUpload", undefined, { message: "Error preparing files for upload" });
      activeUploads.delete(version);
      if (!intervalId) {
        intervalId = setInterval(loadUploads, 15000);
      }
    } finally {
      isUploadInProgress = false; // Always reset the flag when upload completes or fails
    }
  }

  async function handleCancelUpload() {
    if (isUploadInProgress) return; // Prevent cancellation during active upload
    
    pendingUploadVersion = null;
    // Remove the first entry from upload history
    if (uploadHistory?.project_uploads?.length) {
      uploadHistory = {
        ...uploadHistory,
        project_uploads: uploadHistory.project_uploads.slice(1),
      };
    }
    folderFiles = []; // Clear selected files
    if (fileInput) fileInput.value = ""; // Reset the input element
  }

  function createStatusIcon(error: any, timestamps: any): HTMLElement | null {
    if (!timestamps) return null;

    const uploadDate = new Date(timestamps.started);

    if (timestamps.finished) {
      return createSuccessIcon("Finished", "var(--green-600)");
    }

    // Check for failed state
    if (timestamps.failed) {
      const errorMessage = error?.message || "Process failed";
      return createWarningIcon(errorMessage, "var(--red-600)");
    }

    if (timestamps.processing) {
      const pastTime = new Date();
      pastTime.setHours(pastTime.getHours() - 12);
      if (uploadDate < pastTime) {
        return createWarningIcon("Failed to finish process", "var(--red-600)");
      } else {
        return createSpinnerIcon();
      }
    } else {
      const pastTime = new Date();
      pastTime.setHours(pastTime.getHours() - 12);
      if (uploadDate < pastTime) {
        return createWarningIcon("Failed to start process", "var(--red-600)");
      } else {
        return createSpinnerIcon();
      }
    }
  }

  function createWarningIcon(
    message: string,
    color: string
  ): HTMLElement | null {
    const warningSpan = document.createElement("span");
    const warningIcon = document.createElement("i");
    warningIcon.className = "fas fa-warning ml-2";
    warningIcon.style.fontSize = "16px";
    warningIcon.style.color = color;
    warningIcon.title = message;
    warningSpan.appendChild(warningIcon);
    return warningSpan;
  }

  function createSuccessIcon(
    message: string,
    color: string
  ): HTMLElement | null {
    const successSpan = document.createElement("span");
    const successIcon = document.createElement("i");
    successIcon.className = "fas fa-check ml-2";
    successIcon.style.fontSize = "16px";
    successIcon.style.color = color;
    successSpan.appendChild(successIcon);
    return successSpan;
  }

  function createSpinnerIcon(): HTMLElement | null {
    const spinnerSpan = document.createElement("span");
    spinnerSpan.className = "fas fa-spinner fa-spin ml-2";
    spinnerSpan.style.fontSize = "16px";
    spinnerSpan.style.color = "var(--primary-600)";
    return spinnerSpan;
  }
</script>

<Card class="app-mutation-area max-w-lg">
  <div class="project-info">
    <h4 class="card-label-text">Project</h4>
    <div class="card-text">{project?.name || ""}</div>
    <!-- <div class="card-text">{project?.title || ""}</div> -->
  </div>

  <!-- Combined Upload Area -->
  <label
    class="upload-area"
    class:dragging={isDragging}
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
  >
    <input
      bind:this={fileInput}
      type="file"
      webkitdirectory={true}
      directory={true}
      class="hidden"
      on:change={handleFolderSelect}
    />

    <div class="upload-content">
      {#if folderFiles.length === 0}
        <div class="upload-prompt">
          <FontAwesomeIcon icon={faFolder} class="w-8 h-8 mb-2" />
          <p class="text-sm">
            Click to browse a directory<br />
            <span class="card-label-text mt-1">OR</span>
            <br />Drag and drop a folder here
          </p>
        </div>
      {:else}
        <div class="file-list">
          <div class="file-list-header">
            <h5 class="text-sm font-medium">
              Selected Files ({folderFiles.length})
            </h5>
            <p class="text-xs text-gray-500">Click or drop to change folder</p>
          </div>

          {#if folderFiles.length > 0}
            <div class="mt-2 text-center">
              <span class="font-medium">Folder: </span>
              <span class="">{folderFiles[0].path.split("/")[0]}</span>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </label>

  <h4 class="card-label-text mt-2 mb-1">
    {uploadHistory?.project_uploads?.length > 1 ? "Versions" : "Version"}
  </h4>
  <div class="upload-history overflow-y-auto">
    <div class="">
      {#if !uploadHistory?.project_uploads?.length}
        <div class="card-text mt-1">No uploads</div>
      {:else}
        {#each uploadHistory.project_uploads.sort((a, b) => b.version - a.version) as upload}
          <div
            class="flex flex-col upload-version-item {upload.version ===
            pendingUploadVersion
              ? 'pending'
              : ''} {upload.version % 2 === 0 ? 'even' : 'odd'} border-b"
          >
            <div class="text-sm flex items-center gap-2">
              <span class="version">v{upload.version}</span>
              {#if upload.version === pendingUploadVersion}
                {#if !isUploading}
                  <Button
                    size="xs"
                    color="primary"
                    style="min-width: 100px;"
                    class="upload-now-button h-6 ml-2 mt-1.5 relative p-1"
                    on:click={handleUpload}
                    disabled={isUploadInProgress}
                  >
                    <div class="flex items-center">
                      <FontAwesomeIcon icon={faUpload} class="mr-2" />
                      {isUploadInProgress ? 'Uploading...' : 'Upload Now'}
                    </div>
                  </Button>
                  <Button
                    size="xs"
                    color="primary"
                    style="min-width: 24px;"
                    class="upload-cancel-button h-6 mt-1.5 relative p-1"
                    on:click={handleCancelUpload}
                    disabled={isUploadInProgress}
                  >
                    <div class="flex items-center">
                      <FontAwesomeIcon icon={faTimes} />
                    </div>
                  </Button>
                {:else}
                  <div class="w-full">
                    <Progressbar {progress} size="h-4" labelInside />
                  </div>
                {/if}
              {/if}
            </div>
            <div class="upload-card mt-0.5">
              {#if upload.version !== pendingUploadVersion}
                <div class="flex items-center">
                  <span class="value mr-2">
                    {upload.document_count || 0}
                    {upload.document_count === 1 ? "document" : "documents"}
                  </span>
                  <span class="label">{upload.upload_by_name || ""}</span>
                  <span class="value ml-2"
                    >({formatDate(upload.metadata?.timestamps.started)})</span
                  >
                  {@html createStatusIcon(
                    upload.metadata?.error,
                    upload.metadata?.timestamps
                  )?.outerHTML || ""}
                </div>

                <div class="flex justify-between items-center w-full mt-4">
                  <div class="flex flex-col items-center">
                    <div class="footer-label">Uploaded</div>
                    <div class="footer-value">
                      {upload.metadata?.timestamps.uploaded
                        ? formatTime(upload.metadata?.timestamps.uploaded)
                        : "--"}
                    </div>
                  </div>

                  <div class="flex flex-col items-center">
                    <div class="footer-label">Started Processing</div>
                    <div class="footer-value">
                      {upload.metadata?.timestamps.processing
                        ? formatTime(upload.metadata?.timestamps.processing)
                        : "--"}
                    </div>
                  </div>

                  <div class="flex flex-col items-center">
                    {#if upload.metadata?.failed}
                      <div class="footer-label">Failed Processing</div>
                      <div class="footer-value">
                        {upload.metadata?.timestamps.failed
                          ? formatTime(upload.metadata?.timestamps.failed)
                          : "--"}
                      </div>
                    {:else}
                      <div class="footer-label">Finished Processing</div>
                      <div class="footer-value">
                        {upload.metadata?.timestamps.finished
                          ? formatTime(upload.metadata?.timestamps.finished)
                          : "--"}
                      </div>
                    {/if}
                  </div>
                </div>
              {:else}
                <span class="text-sm text-green-600 dark:text-green-400"
                  >Pending</span
                >
              {/if}
            </div>
          </div>
        {/each}
      {/if}
    </div>
  </div>
</Card>

<style>
  :global(.card) {
    border-radius: 0;
    box-shadow: none;
    border: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .project-info {
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
</style>
